import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Chip,
} from '@mui/material';
import { Mail as MailIcon, CheckCircle as CheckCircleIcon, Sync as SyncIcon } from '@mui/icons-material';
import { gmailExpensesService, GmailCredential } from '../services/gmailExpenses';

const GmailConnect: React.FC = () => {
  const [credential, setCredential] = useState<GmailCredential | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadCredentials();
  }, []);

  const loadCredentials = async () => {
    try {
      setLoading(true);
      const cred = await gmailExpensesService.getCredentials();
      setCredential(cred);
    } catch (err: any) {
      setError(err.message || 'Failed to load Gmail credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setError(null);
      const { authorization_url } = await gmailExpensesService.initiateOAuth();
      // Redirect to Gmail OAuth
      window.location.href = authorization_url;
    } catch (err: any) {
      let errorMsg = err.message || 'Failed to initiate Gmail connection';
      // If it's a 503 error with setup instructions, show them
      if (err.response?.data?.setup_instructions) {
        const instructions = err.response.data.setup_instructions;
        errorMsg = `${err.response.data.message}\n\nSetup Instructions:\n${Object.values(instructions).join('\n')}`;
      }
      setError(errorMsg);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      setSuccess(null);
      const result = await gmailExpensesService.syncEmails();
      setSuccess(result.message || 'Email sync started. This may take a few minutes.');
      // Reload credentials to get updated last_sync
      setTimeout(() => {
        loadCredentials();
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to sync emails');
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setError(null);
      await gmailExpensesService.disconnectGmail();
      setCredential(null);
      setSuccess('Gmail account disconnected successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to disconnect Gmail');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <MailIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              <Box>
                <Typography variant="h4" fontWeight={700}>
                  Gmail Expense Tracking
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Connect your Gmail to automatically track expenses from receipts and invoices
                </Typography>
              </Box>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}

            {credential && credential.is_active ? (
              <Box>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CheckCircleIcon />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Gmail Account Connected
                    </Typography>
                  </Box>
                  {credential.last_sync && (
                    <Typography variant="body2">
                      Last synced: {new Date(credential.last_sync).toLocaleString()}
                    </Typography>
                  )}
                </Alert>

                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
                    onClick={handleSync}
                    disabled={syncing}
                  >
                    {syncing ? 'Syncing...' : 'Sync Emails Now'}
                  </Button>
                  <Button variant="outlined" color="error" onClick={handleDisconnect}>
                    Disconnect
                  </Button>
                </Stack>

                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    How it works:
                  </Typography>
                  <Typography variant="body2" component="div">
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      <li>We scan your Gmail for transactional emails (receipts, invoices, bills)</li>
                      <li>Expenses are automatically extracted and categorized</li>
                      <li>Bills and invoices are stored securely in your Bill Vault</li>
                      <li>All data is processed securely and never shared</li>
                    </ul>
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box>
                <Typography variant="body1" paragraph>
                  Connect your Gmail account to automatically track expenses from receipts, invoices, and bills sent to your email.
                </Typography>

                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    What we access:
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Chip label="Read-only access" color="primary" size="small" />
                    <Chip label="Transactional emails only" color="primary" size="small" />
                    <Chip label="No email modification" color="primary" size="small" />
                  </Stack>
                </Box>

                <Button
                  variant="contained"
                  size="large"
                  startIcon={<MailIcon />}
                  onClick={handleConnect}
                  sx={{ mt: 3 }}
                  fullWidth
                >
                  Connect Gmail Account
                </Button>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
};

export default GmailConnect;
